/**
 * use-conversation-channel — realtime-collab-foundation Phase 4 Session 3.
 *
 * Subscribes a React client to a conversation's realtime diff stream. Hooks
 * into the Supabase Realtime broadcast that Phase 3's server-side
 * `RealtimePublisher` emits on `conversation:<id>` every time a mutating tool
 * commits a `recordEdit()`.
 *
 * Responsibilities:
 *  1. **Subscribe** to `conversation:<id>` when `VITE_REALTIME_ENABLED=true`,
 *     parse inbound DiffOp v2 payloads via `diffOpV2Schema.safeParse`
 *     (builder-ssot-consolidation Phase 5 Session 5.3 — publisher emits v2
 *     from Phase 2 forward; ADR 0006 co-exports both schemas).
 *  2. **Dedupe** via a bounded LRU (1000 entries × 10 min TTL) so that
 *     SSE-first-then-Realtime and Realtime-first-then-SSE orderings produce
 *     identical final state.
 *  3. **Gap detection** — when an incoming diff's `prevRevision` doesn't
 *     match `confirmedRevision`, trigger `/resync` BEFORE applying. Handles
 *     the case where a broadcast arrived while we were offline mid-reconnect.
 *  4. **Large-diff fetch** — broadcasts marked `large: true` carry a summary
 *     only (server-side payload degrade); the hook fetches the full row via
 *     `GET /diffs?since=<prevRevision>&limit=1`.
 *  5. **Reconnect** with capped exponential backoff `[1s, 2s, 5s, 10s] × 5`.
 *     After the 5th failure the hook surfaces `status: 'offline'` — the
 *     polling effect (below) takes over seamlessly.
 *  6. **Polling fallback** — runs when `VITE_REALTIME_ENABLED=false` (Q6
 *     build-time flag) OR after 5 consecutive reconnect failures. 5s interval
 *     `/diffs?since=<confirmedRevision>`; drains `hasMore` tails in-tick.
 *  7. **BroadcastChannel** multi-tab presence — posts a heartbeat every 10s
 *     on `arcanum:<conversationId>`; peers that go silent for 25s are pruned.
 *     No state synchronization — each tab subscribes independently. The hook
 *     exposes `otherTabsCount` as a non-UI signal (Q3 — no visible surface
 *     this phase).
 *
 * Never throws. All upstream failures (network, schema parse, Realtime disconnect)
 * are logged at WARN and reconciled via the next `/resync` tick.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import * as Sentry from '@sentry/react';
import { supabase } from '@/lib/supabase';
import { api, ApiError } from '@/lib/api';
import { EditDedupeLRU } from '@/lib/edit-dedupe';
import { queryKeys } from '@/lib/query-keys';
import { captureRateLimited } from '@/lib/sentry-capture-rate-limit';
import { diffOpV2Schema } from '@arcanum-hq/shared-types';
import type { DiffOpV2 } from '@arcanum-hq/shared-types';

const REALTIME_ENABLED = import.meta.env.VITE_REALTIME_ENABLED === 'true';
/**
 * 2026-05-26 — bumped from 5000 to 30000ms after the idle-Builder 429
 * cascade root-cause:
 *   - Old cadence (5s) × 4-6 cache-invalidations per tick = 48-72 reqs/min
 *   - Global standard limiter is 100/15min → exhausted in ~80s of idle polling
 *   - 30s cadence drops baseline to ~8-12 reqs/min, comfortably under budget
 * Polling is the OFFLINE FALLBACK, not the primary path; aggressive cadence
 * stole quota from primary surfaces (model-edit, views) when realtime died.
 */
const POLL_INTERVAL_MS = 30_000;
/**
 * 2026-05-26 — fallback exponential backoff steps when `/diffs` polling hits
 * 429 AND the server does NOT include a `Retry-After` header. Server normally
 * includes it via express-rate-limit `standardHeaders: 'draft-7'`, so this
 * ladder is rarely reached; capped low so a transient blip doesn't lock the
 * loop out longer than necessary.
 */
const POLL_FALLBACK_BACKOFF_MS = [5_000, 15_000, 60_000, 120_000] as const;
/**
 * Maximum cooldown the polling loop will honor — mirrors the `/resync` path's
 * 900_000ms cap (`resyncCooldownUntilRef` line 234). Stops the loop from
 * sitting silent for hours if the server ever requests an absurd Retry-After.
 */
const POLL_COOLDOWN_MAX_MS = 900_000;
const BROADCAST_HEARTBEAT_MS = 10_000;
const BROADCAST_STALE_MS = 25_000;
const RECONNECT_BACKOFF_MS = [1000, 2000, 5000, 10_000] as const;
const RECONNECT_MAX_ATTEMPTS = 5;
/**
 * 2026-06-09 — debounce window for the focus/visibility resume probe
 * (`handleResume`). Returning to a long-idle tab commonly fires BOTH
 * `visibilitychange:visible` AND `window.focus` in the same tick (and some
 * window managers fire `focus` repeatedly while the user settles), and each
 * fire does a full WS `disconnect()`+`connect()` teardown PLUS a `/resync`.
 * Collapsing repeated resumes inside this window stops the wake-flutter from
 * stacking socket bounces + resync→invalidation sweeps onto the
 * per-conversation reads limiter (120/min). Pairs with the leaf-scoped
 * `/resync`-success invalidation below (no longer fans `detail(id)` non-exact
 * across views + active-version-sync + model-view + versions).
 */
const RESUME_DEBOUNCE_MS = 3000;
const DEDUPE_MAX_SIZE = 1000;
const DEDUPE_TTL_MS = 10 * 60 * 1000;
/**
 * Safety cap on polling-tick tail-drain recursion. A pathological server
 * returning `hasMore: true` forever would otherwise recurse unboundedly
 * within one tick. At limit=50 per page, 20 iterations drains 1000 diffs —
 * more than enough for any realistic catch-up; the NEXT interval tick picks
 * up whatever remains.
 */
const POLL_MAX_DRAIN_ITERS = 20;

export type RealtimeStatus =
  | 'connected'
  | 'reconnecting'
  | 'polling'
  | 'offline';

/**
 * REST `/resync` health, orthogonal to `RealtimeStatus` (which tracks the WS
 * connection only). Goes `syncing` while a `/resync` cooldown is active
 * (server returned 429 OR a network error) and clears back to `idle` on the
 * next successful resync. The badge surfaces this so a passive viewer sees
 * "Syncing…" while the per-conversation rate-limit window drains during a
 * cold-start storm, even though the WS itself is `connected`.
 */
export type SyncStatus = 'idle' | 'syncing';

export interface UseConversationChannelReturn {
  /**
   * Connection mode. `polling` if the build-time flag
   * `VITE_REALTIME_ENABLED !== 'true'` (Q6 — polling is the only path) OR
   * after `RECONNECT_MAX_ATTEMPTS` WS failures while still on the offline
   * fallback; `connected` if WS subscribed; `reconnecting` while a backoff
   * retry is queued; `offline` immediately after the 5th reconnect failure,
   * before the polling effect takes over.
   */
  realtimeStatus: RealtimeStatus;
  /**
   * `idle` normally; `syncing` while a `/resync` cooldown is active after a
   * 429 or network error. Reset to `idle` on the next successful `/resync`
   * (or when `consecutiveFailures` hits 0). Orthogonal to `realtimeStatus`
   * — the WS may stay `connected` while REST `/resync` is in cooldown.
   * Resilience-and-observability-hardening Phase 1.5 (UX polish).
   */
  syncStatus: SyncStatus;
  /**
   * Highest revision the client has observed + applied. Starts at
   * `options.initialRevision ?? 0` and advances monotonically.
   */
  confirmedRevision: number;
  /**
   * Count of OTHER browser tabs currently subscribed to the same conversation
   * (via BroadcastChannel heartbeat). `0` if this is the only tab OR if
   * BroadcastChannel is unavailable (e.g. SSR, older browsers, tests without
   * a polyfill).
   */
  otherTabsCount: number;
  /**
   * Force an immediate `/resync` from the current `confirmedRevision`.
   * Exposed for debugging + downstream feature use (e.g. a "Refresh" button).
   * Session 4's Builder wiring does NOT call this — the hook reconciles
   * automatically on reconnect + gap detection.
   */
  resyncNow: () => Promise<void>;
}

export interface UseConversationChannelOptions {
  /**
   * Called for Realtime broadcast diffs (DiffOpV2 — builder-ssot-consolidation
   * Phase 5 Session 5.3). Polling/resync catch-up paths now invalidate
   * queryKeys.conversations.views + .detail directly (ADR-0021 Option B) —
   * they do NOT fan diffs to this callback. ADR 0006 / ADR 0017 / ADR 0021.
   *
   * Contract: fires AFTER the dedupe set is updated, so `confirmedRevision`
   * has already advanced and downstream invalidations are safe.
   */
  onDiffApplied: (diff: DiffOpV2) => void;

  /**
   * Called when `/resync` returns a snapshot payload (large-gap branch). Rare
   * — only fires on cold clients or after very long disconnection. The
   * consumer should swap cached agent state entirely rather than diff-apply.
   */
  onSnapshotApplied: (snapshot: {
    agentState: unknown;
    revision: number;
  }) => void;

  /**
   * 2026-05-25 — Fires when the server broadcasts `version_changed` on
   * `conversation:<id>` (emitted by createConversationVersion /
   * restoreConversationVersion / acceptUnderwriting). Carries:
   *   { conversationId, kind, versionId, label, ts }
   *
   * Cross-tab convergence: when tab A creates/restores/accepts a version,
   * tab B receives this broadcast and the hook auto-invalidates
   * `conversations.all` (queries refetch). Consumers wire this callback when
   * they hold non-React-Query state that also needs reset — the Builder
   * route uses it to clear `liveMetrics` + the `metrics:<convId>`
   * localStorage cache so the Performance / Hero / Signal panels re-seed
   * from the fresh /model-view cellValues. Optional — the broadcast still
   * fires the cache invalidation when this is undefined.
   */
  onVersionChanged?: (payload: {
    conversationId: string;
    kind: 'created' | 'restored' | 'accepted';
    versionId: string;
    label: string;
  }) => void;

  /**
   * Initial `confirmedRevision`. Typically the `currentRevision` field on the
   * conversation-detail response (see `conversations.service.v2.ts::get`
   * Q1 resolution). Omit / 0 for cold clients — the hook triggers an initial
   * `/resync` on first SUBSCRIBED callback to catch up.
   */
  initialRevision?: number;
}

export function useConversationChannel(
  conversationId: string | null,
  options: UseConversationChannelOptions
): UseConversationChannelReturn {
  const queryClient = useQueryClient();

  const [realtimeStatus, setRealtimeStatus] = useState<RealtimeStatus>(() => {
    if (!REALTIME_ENABLED) return 'polling';
    return 'reconnecting';
  });
  const [confirmedRevision, setConfirmedRevision] = useState(
    options.initialRevision ?? 0
  );
  const [otherTabsCount, setOtherTabsCount] = useState(0);
  const [syncStatus, setSyncStatus] = useState<SyncStatus>('idle');

  // Refs avoid stale-closure issues in event handlers. None drive renders.
  const dedupeRef = useRef<EditDedupeLRU>(
    new EditDedupeLRU({ maxSize: DEDUPE_MAX_SIZE, ttlMs: DEDUPE_TTL_MS })
  );
  // `confirmedRevisionRef` is the AUTHORITATIVE revision for the async
  // gap-detection / resync paths — NOT a render-time mirror of state. It is
  // advanced SYNCHRONOUSLY via `commitRevision` so a burst of inbound diffs
  // (which all run before React re-renders) sees each advance immediately and
  // does not re-detect the same gap → one resync per real gap, not one per
  // broadcast. (Pre-fix it was reassigned every render — `current = state` —
  // so synchronous advances were invisible mid-burst, producing a /resync 429
  // storm under cold-start gaps.)
  const confirmedRevisionRef = useRef(confirmedRevision);
  const optionsRef = useRef(options);
  optionsRef.current = options;
  const otherTabsRef = useRef<Map<string, number>>(new Map());

  // Resync convergence guards (fix: non-converging /resync 429 storm).
  // `resyncInFlightRef` coalesces concurrent gap-detections into ONE request;
  // `resyncCooldownUntilRef` + `resyncBackoffRef` apply capped exponential
  // backoff after a failure (esp. 429) so the next broadcast does not
  // immediately re-fire into the rate limiter.
  const resyncInFlightRef = useRef<Promise<void> | null>(null);
  const resyncCooldownUntilRef = useRef(0);
  const resyncBackoffRef = useRef(0);
  /**
   * 2026-05-26 — polling-path cooldown / backoff state. Mirrors the `/resync`
   * trio above. Pre-fix the polling loop had zero rate-limit awareness: it
   * fired `/diffs` + unconditional invalidations every 5s, blew the global
   * standard limiter (100/15min) in ~80s of idle polling, and kept hammering
   * because the bare catch logged + continued. This trio:
   *   - `pollCooldownUntilRef` — epoch ms when the next tick may fire
   *   - `pollBackoffStepRef`  — index into POLL_FALLBACK_BACKOFF_MS for
   *                              servers that don't return Retry-After
   *   - `pollInFlightRef`     — coalesces overlapping ticks (a long tick that
   *                              outlives its interval shouldn't double up)
   */
  const pollCooldownUntilRef = useRef(0);
  const pollBackoffStepRef = useRef(0);
  const pollInFlightRef = useRef<Promise<void> | null>(null);
  // NEW Phase 3 § 3.1 — increment in catch, reset in try-success branch.
  // Increment ONCE per coalesced /resync request (resyncInFlightRef
  // coalesces N concurrent gap-detections into ONE; catch fires once per
  // coalesced request, so a single increment is correct).
  const consecutiveFailuresRef = useRef(0);
  // Phase 1.5 — schedule a one-shot retry when cooldown elapses so passively-
  // viewed (idle) conversations recover without waiting for a natural broadcast
  // or page reload. Cleared on success, on each new failure (re-armed with the
  // new backoff), and on conversation/unmount cleanup.
  const resyncRetryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(
    null
  );
  const clearResyncRetryTimer = useCallback(() => {
    if (resyncRetryTimerRef.current) {
      clearTimeout(resyncRetryTimerRef.current);
      resyncRetryTimerRef.current = null;
    }
  }, []);
  // Ref-based self-reference: resyncFromConfirmed needs to schedule a timer
  // that calls itself, but useCallback's deps can't include the callback
  // itself. The effect below keeps the ref pointing at the current closure.
  const resyncFromConfirmedRef = useRef<() => Promise<void>>(() =>
    Promise.resolve()
  );

  // Advance both the authoritative ref (synchronously, for the async paths)
  // and the state (for consumers + re-render). Stable identity (no deps).
  const commitRevision = useCallback((revision: number) => {
    confirmedRevisionRef.current = revision;
    setConfirmedRevision(revision);
  }, []);

  // ----------------------------------------------------------------
  // /resync — gap fill on reconnect, cold client seed, explicit call.
  // Always reads confirmedRevision from ref to avoid stale-closure bugs.
  // ----------------------------------------------------------------
  const resyncFromConfirmed = useCallback((): Promise<void> => {
    if (!conversationId) return Promise.resolve();
    // Coalesce: if a resync is already running, every concurrent caller awaits
    // the SAME request instead of firing its own (kills the burst → 429 storm).
    if (resyncInFlightRef.current) return resyncInFlightRef.current;
    // Backoff: after a recent failure, skip until the cooldown elapses. The
    // caller (applyDiffV2) still applies the live diff forward, so progress is
    // not blocked — only the redundant resyncs are suppressed.
    if (Date.now() < resyncCooldownUntilRef.current) return Promise.resolve();

    const run = (async (): Promise<void> => {
      try {
        const res = await api.resync(
          conversationId,
          confirmedRevisionRef.current
        );
        if ('snapshot' in res) {
          optionsRef.current.onSnapshotApplied(res.snapshot);
          commitRevision(res.fromRevision);
          // Snapshot replaces state — prior dedupe entries are no longer relevant.
          dedupeRef.current.clear();
        } else {
          // ADR-0021 Option B: small-gap catch-up via invalidation, not diff fan-out.
          // The /resync endpoint emits diffs:[] (S7.4); even if it returned v1 diffs
          // we no longer fan them to onDiffApplied — invalidate instead so
          // useConversationViews refetches and reseeds the grid.
          //
          // 2026-06-09 — invalidate the SPECIFIC leaf keys only (views + the
          // detail LEAF), NOT the non-exact `detail(id)` prefix. `views` AND
          // `active-version-sync` AND `model-view` AND `versions` are all
          // nested CHILDREN of `detail(id)` (see query-keys.ts), so a non-exact
          // `detail(id)` invalidation prefix-matches and refetches ALL of them
          // per resync — the exact cascade that blows the 120/min
          // per-conversation reads limiter on idle-tab resume (a wake fires
          // multiple resyncs). `exact: true` confines this to the detail-shim
          // read. Mirrors the polling-tick guard (B) below, which already
          // does this; the resync + onDiffApplied paths had kept the footgun.
          queryClient.invalidateQueries({
            queryKey: queryKeys.conversations.views(conversationId),
          });
          queryClient.invalidateQueries({
            queryKey: queryKeys.conversations.detail(conversationId),
            exact: true,
          });
          commitRevision(res.toRevision);
        }
        // Success → clear backoff + reset failure counter.
        resyncBackoffRef.current = 0;
        resyncCooldownUntilRef.current = 0;
        consecutiveFailuresRef.current = 0; // NEW Phase 3 § 3.1
        setSyncStatus('idle'); // Phase 1.5 — clear "Syncing…" badge
        clearResyncRetryTimer(); // Phase 1.5 — no retry needed after success
      } catch (e) {
        // Prefer the server's authoritative Retry-After (sent on the 429 by the
        // per-conversation resyncRateLimit, draft-7) — cooperative backoff.
        // Otherwise fall back to capped exponential backoff (1s → 2s → … → 15s)
        // so a burst of broadcasts doesn't re-fire /resync into the limiter.
        // Either way, leave confirmedRevision + dedupe untouched; live diffs
        // still heal the revision forward during the cooldown.
        // Duck-typed (not `instanceof ApiError`) so the field is read without
        // coupling the hook to the api module's class identity.
        const retryAfterSeconds = (e as { retryAfterSeconds?: unknown })
          ?.retryAfterSeconds;
        const retryAfterMs =
          typeof retryAfterSeconds === 'number' && retryAfterSeconds >= 0
            ? Math.min(retryAfterSeconds * 1000, 900_000)
            : null;
        if (retryAfterMs != null) {
          resyncBackoffRef.current = retryAfterMs;
        } else {
          resyncBackoffRef.current = Math.min(
            resyncBackoffRef.current ? resyncBackoffRef.current * 2 : 1000,
            15000
          );
        }
        resyncCooldownUntilRef.current = Date.now() + resyncBackoffRef.current;
        // Phase 1.5 — schedule a one-shot retry when cooldown elapses so
        // passively-viewed conversations don't wait for a natural broadcast
        // (50ms buffer so the cooldown check inside resyncFromConfirmed sees
        // Date.now() > resyncCooldownUntilRef.current).
        clearResyncRetryTimer();
        resyncRetryTimerRef.current = setTimeout(() => {
          resyncRetryTimerRef.current = null;
          void resyncFromConfirmedRef.current();
        }, resyncBackoffRef.current + 50);
        // NEW Phase 3 § 3.1 — increment failure counter + capture if sustained.
        consecutiveFailuresRef.current += 1;
        setSyncStatus('syncing'); // Phase 1.5 — surface "Syncing…" badge
        if (consecutiveFailuresRef.current >= 3) {
          captureRateLimited('realtime_resync_sustained_backoff', () => {
            Sentry.captureMessage('realtime_resync_sustained_backoff', {
              level: 'warning',
              tags: { surface: 'realtime-resync' },
              extra: {
                conversation_id: conversationId,
                backoff_ms: resyncBackoffRef.current,
                consecutive_failures: consecutiveFailuresRef.current,
              },
            });
          });
        }
        console.warn(
          `[realtime] /resync failed; backing off ${resyncBackoffRef.current}ms`,
          e
        );
      } finally {
        resyncInFlightRef.current = null;
      }
    })();
    resyncInFlightRef.current = run;
    return run;
  }, [conversationId, commitRevision, queryClient, clearResyncRetryTimer]);

  // Phase 1.5 — keep the self-reference ref pointing at the current closure so
  // the scheduled retry timer always invokes the latest resyncFromConfirmed.
  resyncFromConfirmedRef.current = resyncFromConfirmed;

  // Phase 1.5 — clear any pending recovery timer on conversation switch /
  // unmount so a stale timer doesn't fire against an old conversation.
  useEffect(() => {
    return () => {
      clearResyncRetryTimer();
    };
  }, [conversationId, clearResyncRetryTimer]);

  // ----------------------------------------------------------------
  // Apply one inbound DiffOp v2 (Realtime broadcast path).
  // builder-ssot-consolidation Phase 5 Session 5.3 — uses diffOpV2Schema.
  // Closure-staleness: setQueryData functional updater in builder.tsx's
  // onDiffApplied reads the LATEST views cache (not a closure snapshot),
  // so this does NOT reintroduce the stale-wipe bug the inputValuesRef guards
  // against (research § 11 risk; apps/web/CLAUDE.md "Realtime echo closure-
  // staleness pattern"). ADR 0017.
  // ----------------------------------------------------------------
  const applyDiffV2 = useCallback(
    async (diff: DiffOpV2): Promise<void> => {
      if (!conversationId) return;
      if (dedupeRef.current.has(diff.editId)) return; // dedupe — no-op
      dedupeRef.current.add(diff.editId);

      // Gap detection: if prevRevision !== confirmedRevision we've lost a
      // broadcast. Resync through the gap before applying this one.
      if (diff.prevRevision !== confirmedRevisionRef.current) {
        await resyncFromConfirmed();
        // After resync we may already be past this diff.
        if (diff.revision <= confirmedRevisionRef.current) return;
      }

      if (diff.large) {
        // Summary-only broadcast — full diff requires REST fetch. For the v2
        // path the REST endpoint still returns v1 DiffOps; rather than adapt
        // v1→v2 on the rare large-diff branch, the builder's onSnapshotApplied
        // invalidates views(id) which triggers a full refetch. Emit via the
        // snapshot path so the consumer can choose its strategy.
        optionsRef.current.onSnapshotApplied({
          agentState: null,
          revision: diff.revision,
        });
        commitRevision(diff.revision);
        return;
      }

      optionsRef.current.onDiffApplied(diff);
      commitRevision(diff.revision);
    },
    [conversationId, resyncFromConfirmed, commitRevision]
  );

  // ----------------------------------------------------------------
  // Realtime subscription lifecycle — runs only when REALTIME_ENABLED.
  // ----------------------------------------------------------------
  useEffect(() => {
    if (!conversationId || !REALTIME_ENABLED) return;

    let disposed = false;
    let reconnectAttempt = 0;
    let pendingTimer: ReturnType<typeof setTimeout> | null = null;
    let currentChannel: ReturnType<typeof supabase.channel> | null = null;

    const subscribe = async (): Promise<void> => {
      if (disposed) return;
      const topic = `conversation:${conversationId}`;
      // `private: true` opts into Supabase Realtime's RLS-gated broadcast
      // path. The 3 policies on `realtime.messages` (migration
      // `20260421000001_realtime_messages_rls.sql`) gate SELECT to the
      // conversation owner; the publisher (`apps/api/src/shared/realtime-publisher.ts`)
      // matches with `{ config: { private: true } }` so its `channel.send()`
      // INSERTs into `realtime.messages` for cross-client propagation. Without
      // this flag, the channel is public and broadcasts from a non-subscribed
      // service-role publisher silently fail to reach user-JWT subscribers on
      // other cluster nodes (root-cause of the cross-tab regression caught by
      // the Phase 5 dev walkthrough, 2026-04-25).
      const channel = supabase.channel(topic, { config: { private: true } });
      currentChannel = channel;

      channel.on(
        'broadcast',
        { event: 'diff' },
        (payload: { payload?: unknown }) => {
          if (disposed) return;
          // builder-ssot-consolidation Phase 5 Session 5.3: decode via
          // diffOpV2Schema (publisher emits v2 from Phase 2 forward; ADR 0006).
          // Cost (~200µs) negligible vs WS RTT. Malformed payloads log + drop;
          // confirmedRevision is untouched so the next /resync reconciles.
          const parsed = diffOpV2Schema.safeParse(payload.payload);
          if (!parsed.success) {
            console.warn(
              '[realtime] inbound DiffOp v2 failed schema validation — dropping',
              parsed.error.issues
            );
            return;
          }
          void applyDiffV2(parsed.data);
        }
      );

      // 2026-05-25 — `version_changed` broadcast on create / restore / accept
      // (published by conversations.service.v2.ts:publishVersionChanged).
      // The server-side broadcast lets OTHER tabs of the same user converge
      // their cached state without waiting for the next /resync poll or
      // user-triggered action. Same-tab also receives this echo (it's a
      // separate user-JWT subscriber from the admin-role publisher), making
      // the cleanup idempotent across both paths.
      channel.on(
        'broadcast',
        { event: 'version_changed' },
        (payload: { payload?: unknown }) => {
          if (disposed) return;
          const raw = payload.payload;
          if (
            raw == null ||
            typeof raw !== 'object' ||
            !('kind' in raw) ||
            !('versionId' in raw) ||
            !('label' in raw)
          ) {
            console.warn(
              '[realtime] inbound version_changed missing required fields — dropping',
              raw
            );
            return;
          }
          const detail = raw as {
            conversationId: string;
            kind: 'created' | 'restored' | 'accepted';
            versionId: string;
            label: string;
          };
          // Invalidate the entire conversations key so detail + views +
          // versions all refetch (matches the useRestoreVersion mutation's
          // own onSuccess shape). The route hooks optionally clears any
          // non-React-Query state (liveMetrics, localStorage caches).
          queryClient.invalidateQueries({
            queryKey: queryKeys.conversations.all,
          });
          optionsRef.current.onVersionChanged?.(detail);
        }
      );

      channel.subscribe(async (status) => {
        if (disposed) return;
        if (status === 'SUBSCRIBED') {
          setRealtimeStatus('connected');
          reconnectAttempt = 0;
          // First subscribe (or resubscribe) triggers /resync from
          // confirmedRevision — catches anything that fell during the
          // disconnect window. Cheap when caught up (empty diffs[]).
          await resyncFromConfirmed();
        } else if (
          status === 'CHANNEL_ERROR' ||
          status === 'CLOSED' ||
          status === 'TIMED_OUT'
        ) {
          // M1 fix: check the give-up gate BEFORE scheduling another retry
          // so the 5th failure doesn't both mark 'offline' AND queue a 6th
          // subscribe that would race the polling effect.
          if (reconnectAttempt >= RECONNECT_MAX_ATTEMPTS) {
            setRealtimeStatus('offline');
            return;
          }
          setRealtimeStatus('reconnecting');
          const delay =
            RECONNECT_BACKOFF_MS[
              Math.min(reconnectAttempt, RECONNECT_BACKOFF_MS.length - 1)
            ];
          reconnectAttempt++;
          pendingTimer = setTimeout(() => {
            if (disposed) return;
            // eslint-disable-next-line @typescript-eslint/no-empty-function
            void channel.unsubscribe().catch(() => {});
            void subscribe();
          }, delay);
        }
      });
    };

    void subscribe();

    // 2026-05-27 — proactive resume probe. Pre-fix, an idle tab whose WS
    // silently died (Chrome throttles backgrounded WS to 1Hz and Supabase
    // Realtime times the channel out after ~30s of missed heartbeat) would
    // stay "Offline" until the 30s polling interval discovered the gap or
    // the user-initiated action triggered a fresh broadcast. On `focus` /
    // `visibilitychange:visible`, we (a) trigger a /resync from
    // confirmedRevision so the cache catches up if we missed broadcasts,
    // and (b) bounce the realtime socket so a stale WS surfaces
    // CHANNEL_ERROR immediately and re-subscribes within the standard
    // 1s/2s/5s/10s backoff ladder. Both operations are cheap (resync is
    // backoff-gated; reconnect is ~100ms and emits no extra API calls).
    let lastResumeAt = 0;
    const handleResume = (): void => {
      if (typeof document === 'undefined') return;
      if (document.visibilityState !== 'visible') return;
      if (disposed) return;
      // Debounce wake-flutter: a single return-to-tab commonly fires both
      // `visibilitychange:visible` and `window.focus` (and repeated `focus`
      // edges), each of which would otherwise bounce the socket + fire a
      // /resync. Collapse resumes inside RESUME_DEBOUNCE_MS to one.
      const now = Date.now();
      if (now - lastResumeAt < RESUME_DEBOUNCE_MS) return;
      lastResumeAt = now;
      void resyncFromConfirmed();
      try {
        supabase.realtime.disconnect();
        supabase.realtime.connect();
      } catch {
        // disconnect/connect throw on a never-initialized transport — safe
        // to swallow; the next subscribe call will re-establish.
      }
    };
    document.addEventListener('visibilitychange', handleResume);
    window.addEventListener('focus', handleResume);

    return () => {
      disposed = true;
      document.removeEventListener('visibilitychange', handleResume);
      window.removeEventListener('focus', handleResume);
      if (pendingTimer) clearTimeout(pendingTimer);
      // eslint-disable-next-line @typescript-eslint/no-empty-function
      if (currentChannel) void currentChannel.unsubscribe().catch(() => {});
    };
  }, [conversationId, applyDiffV2, resyncFromConfirmed]);

  // ----------------------------------------------------------------
  // Polling fallback — runs when the flag is off OR status is 'offline'.
  //
  // 2026-05-26 — three independent guards added after the idle-Builder 429
  // cascade post-mortem:
  //
  //   (A) Cooldown / backoff on 429. The pre-fix loop's bare catch would
  //       log + keep ticking every 5s, burning quota faster than the global
  //       limiter's 15-min window could roll. Now honors `Retry-After` from
  //       `ApiError.retryAfterSeconds` (capped at POLL_COOLDOWN_MAX_MS),
  //       falls back to a 4-step exponential ladder when the header isn't
  //       present, and resets on the next success.
  //
  //   (B) Conditional invalidation. Pre-fix the tick invalidated `views` +
  //       `detail` UNCONDITIONALLY. `detail` is a 2-element key
  //       (`[conversations, id]`) that prefix-matches `views` AND `diffs`
  //       AND `model-view` AND `active-version-sync` AND `versions` — so
  //       one tick fan-out cascaded into 4-6 backend requests with no new
  //       data to apply. Now we only invalidate the SPECIFIC views/detail
  //       leaf keys (not the prefix) AND only when `res.diffs.length > 0`
  //       (the whole point of the catch-up was new data; if `/diffs`
  //       returns empty there's nothing for downstream caches to learn).
  //
  //   (C) In-flight coalescing. A long tick that outlives POLL_INTERVAL_MS
  //       must not stack a second tick on top of itself — pollInFlightRef
  //       skips overlapping ticks (mirrors resyncInFlightRef pattern).
  // ----------------------------------------------------------------
  useEffect(() => {
    if (!conversationId) return;
    const shouldPoll = !REALTIME_ENABLED || realtimeStatus === 'offline';
    if (!shouldPoll) return;

    if (!REALTIME_ENABLED) setRealtimeStatus('polling');
    let disposed = false;

    const tick = async (depth = 0): Promise<void> => {
      if (disposed) return;
      // Guard (C): coalesce overlapping ticks.
      if (pollInFlightRef.current) return;
      // Guard (A): cooldown gate — skip until the cooldown deadline elapses.
      if (Date.now() < pollCooldownUntilRef.current) return;

      const run = async (): Promise<void> => {
        try {
          const res = await api.getDiffs(
            conversationId,
            confirmedRevisionRef.current,
            50
          );
          // Success → reset backoff step + clear any lingering cooldown.
          pollBackoffStepRef.current = 0;
          pollCooldownUntilRef.current = 0;

          commitRevision(res.currentRevision);

          // Guard (B): only invalidate when /diffs actually carried new data.
          // ADR-0021 Option B: catch-up via invalidation, not diff fan-out.
          // We invalidate the SPECIFIC leaf keys (views + diffs) — NOT the
          // 2-element parent key conversations.detail(id), which would
          // prefix-match active-version-sync / model-view / versions and
          // re-fire 4 more requests per tick (the original cascade footgun).
          if (res.diffs.length > 0) {
            queryClient.invalidateQueries({
              queryKey: queryKeys.conversations.views(conversationId),
            });
            // Refetch only the `detail` LEAF (the conversation row +
            // messages), not its prefix-matching children. exact:true
            // ensures TanStack matches only `[conversations, id]` and not
            // any longer key sharing that prefix.
            queryClient.invalidateQueries({
              queryKey: queryKeys.conversations.detail(conversationId),
              exact: true,
            });
          }

          // Drain `hasMore` tails within the same tick so the next interval
          // firing starts from the fully-caught-up revision. Capped at
          // POLL_MAX_DRAIN_ITERS so a pathological server returning hasMore
          // forever doesn't recurse unboundedly — the next setInterval tick
          // picks up whatever remains. Each recursive tick gets its own
          // pollInFlightRef bookkeeping via the outer wrapper.
          if (res.hasMore && !disposed && depth + 1 < POLL_MAX_DRAIN_ITERS) {
            // Re-enter directly; pollInFlightRef is released after the outer
            // call's `finally` so reset+re-set is safe.
            pollInFlightRef.current = null;
            await tick(depth + 1);
          }
        } catch (e) {
          // Guard (A): compute cooldown from Retry-After if present, else
          // step through the exponential ladder. Either way, set the next
          // earliest-allowed tick time so the interval skips until ready.
          const retryAfterMs =
            e instanceof ApiError && e.retryAfterSeconds
              ? Math.min(e.retryAfterSeconds * 1000, POLL_COOLDOWN_MAX_MS)
              : POLL_FALLBACK_BACKOFF_MS[
                  Math.min(
                    pollBackoffStepRef.current,
                    POLL_FALLBACK_BACKOFF_MS.length - 1
                  )
                ];
          pollCooldownUntilRef.current = Date.now() + retryAfterMs;
          if (pollBackoffStepRef.current < POLL_FALLBACK_BACKOFF_MS.length) {
            pollBackoffStepRef.current += 1;
          }
          console.warn('[realtime] polling /diffs failed; cooldown set', {
            retryAfterMs,
            error: e,
          });
        }
      };

      pollInFlightRef.current = run();
      try {
        await pollInFlightRef.current;
      } finally {
        pollInFlightRef.current = null;
      }
    };

    void tick();
    const interval = setInterval(() => void tick(), POLL_INTERVAL_MS);
    return () => {
      disposed = true;
      clearInterval(interval);
    };
  }, [conversationId, realtimeStatus]);

  // ----------------------------------------------------------------
  // BroadcastChannel — multi-tab presence hint only (no state sync).
  // Q3: no UI surface this phase. `otherTabsCount` is a non-UI signal
  // exposed for future consumption.
  // ----------------------------------------------------------------
  useEffect(() => {
    if (!conversationId || typeof BroadcastChannel === 'undefined') return;

    const tabId = crypto.randomUUID();
    const bc = new BroadcastChannel(`arcanum:${conversationId}`);

    const sendHeartbeat = (): void => {
      bc.postMessage({ type: 'tab-active', tabId, timestamp: Date.now() });
    };

    const pruneAndUpdate = (): void => {
      const now = Date.now();
      for (const [id, ts] of otherTabsRef.current) {
        if (now - ts > BROADCAST_STALE_MS) otherTabsRef.current.delete(id);
      }
      setOtherTabsCount(otherTabsRef.current.size);
    };

    bc.onmessage = (event: MessageEvent) => {
      const msg = event.data as {
        type?: string;
        tabId?: string;
        timestamp?: number;
      };
      if (
        msg?.type === 'tab-active' &&
        typeof msg.tabId === 'string' &&
        typeof msg.timestamp === 'number' &&
        msg.tabId !== tabId
      ) {
        otherTabsRef.current.set(msg.tabId, msg.timestamp);
        pruneAndUpdate();
      }
    };

    sendHeartbeat();
    const interval = setInterval(() => {
      sendHeartbeat();
      pruneAndUpdate();
    }, BROADCAST_HEARTBEAT_MS);

    return () => {
      clearInterval(interval);
      bc.close();
      otherTabsRef.current.clear();
      setOtherTabsCount(0);
    };
  }, [conversationId]);

  return {
    realtimeStatus,
    syncStatus,
    confirmedRevision,
    otherTabsCount,
    resyncNow: resyncFromConfirmed,
  };
}
